/**
 * Person Aggregate Root
 * 
 * Enterprise-grade DDD implementation
 * Single aggregate file - no duplicate entity files!
 */
import { AggregateRootBase } from '../../../../shared/domain/AggregateRootBase';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export class PersonName {
  constructor(
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly middleName?: string
  ) {
    if (!firstName || firstName.trim().length === 0) {
      throw new Error('First name is required');
    }
    if (!lastName || lastName.trim().length === 0) {
      throw new Error('Last name is required');
    }
  }

  getFullName(): string {
    return this.middleName 
      ? `${this.firstName} ${this.middleName} ${this.lastName}`
      : `${this.firstName} ${this.lastName}`;
  }

  equals(other: PersonName): boolean {
    return this.firstName === other.firstName &&
           this.lastName === other.lastName &&
           this.middleName === other.middleName;
  }
}

export class EmailAddress {
  constructor(public readonly value: string) {
    if (!this.isValid(value)) {
      throw new Error(`Invalid email: ${value}`);
    }
  }

  private isValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  equals(other: EmailAddress): boolean {
    return this.value.toLowerCase() === other.value.toLowerCase();
  }
}

// Domain Events
export interface PersonCreated {
  aggregateId: string;
  email: string;
  firstName: string;
  lastName: string;
  roleType: string;
  occurredAt: Date;
}

export interface PersonUpdated {
  aggregateId: string;
  changedFields: string[];
  occurredAt: Date;
}

export interface PersonDeactivated {
  aggregateId: string;
  reason: string;
  occurredAt: Date;
}

export interface PersonReactivated {
  aggregateId: string;
  occurredAt: Date;
}

/**
 * Person Aggregate Root
 * Core entity in the Party bounded context
 */
export class Person extends AggregateRootBase {
  private constructor(
    id: string,
    private name: PersonName,
    private email: EmailAddress,
    private roleType: 'Human' | 'AiAgent',
    private isActive: boolean,
    private jobTitle?: string,
    private department?: string,
    private organizationId?: string,
    private createdAt?: Date,
    private updatedAt?: Date,
    version: number = 0
  ) {
    super(id, version);
  }

  /**
   * Factory method to create new Person
   */
  static create(
    firstName: string,
    lastName: string,
    email: string,
    roleType: 'Human' | 'AiAgent' = 'Human',
    middleName?: string
  ): Person {
    const id = uuidv4();
    const name = new PersonName(firstName, lastName, middleName);
    const emailVO = new EmailAddress(email);
    const now = new Date();

    const person = new Person(
      id,
      name,
      emailVO,
      roleType,
      true, // isActive
      undefined, // jobTitle
      undefined, // department
      undefined, // organizationId
      now,
      now
    );

    person.addDomainEvent<PersonCreated>({
      aggregateId: id,
      email,
      firstName,
      lastName,
      roleType,
      occurredAt: now
    });

    return person;
  }

  /**
   * Reconstitute from persistence
   */
  static fromSnapshot(snapshot: any): Person {
    return new Person(
      snapshot.id,
      new PersonName(
        snapshot.firstName,
        snapshot.lastName,
        snapshot.middleName
      ),
      new EmailAddress(snapshot.email),
      snapshot.roleType,
      snapshot.isActive,
      snapshot.jobTitle,
      snapshot.department,
      snapshot.organizationId,
      snapshot.createdAt,
      snapshot.updatedAt,
      snapshot.version
    );
  }

  /**
   * Update personal information
   */
  updatePersonalInfo(
    firstName?: string,
    lastName?: string,
    middleName?: string
  ): void {
    const changedFields: string[] = [];

    if (firstName && firstName !== this.name.firstName) {
      changedFields.push('firstName');
    }
    if (lastName && lastName !== this.name.lastName) {
      changedFields.push('lastName');
    }
    if (middleName !== undefined && middleName !== this.name.middleName) {
      changedFields.push('middleName');
    }

    if (changedFields.length > 0) {
      this.name = new PersonName(
        firstName || this.name.firstName,
        lastName || this.name.lastName,
        middleName !== undefined ? middleName : this.name.middleName
      );
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<PersonUpdated>({
        aggregateId: this.id,
        changedFields,
        occurredAt: new Date()
      });
    }
  }

  /**
   * Update email address
   */
  updateEmail(newEmail: string): void {
    const newEmailVO = new EmailAddress(newEmail);
    if (!this.email.equals(newEmailVO)) {
      this.email = newEmailVO;
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<PersonUpdated>({
        aggregateId: this.id,
        changedFields: ['email'],
        occurredAt: new Date()
      });
    }
  }

  /**
   * Update job information
   */
  updateJobInfo(jobTitle?: string, department?: string): void {
    const changedFields: string[] = [];

    if (jobTitle !== this.jobTitle) {
      this.jobTitle = jobTitle;
      changedFields.push('jobTitle');
    }

    if (department !== this.department) {
      this.department = department;
      changedFields.push('department');
    }

    if (changedFields.length > 0) {
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<PersonUpdated>({
        aggregateId: this.id,
        changedFields,
        occurredAt: new Date()
      });
    }
  }

  /**
   * Assign to organization
   */
  assignToOrganization(organizationId: string): void {
    if (this.organizationId !== organizationId) {
      this.organizationId = organizationId;
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<PersonUpdated>({
        aggregateId: this.id,
        changedFields: ['organizationId'],
        occurredAt: new Date()
      });
    }
  }

  /**
   * Deactivate person
   */
  deactivate(reason: string): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<PersonDeactivated>({
      aggregateId: this.id,
      reason,
      occurredAt: new Date()
    });
  }

  /**
   * Reactivate person
   */
  reactivate(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<PersonReactivated>({
      aggregateId: this.id,
      occurredAt: new Date()
    });
  }

  // Getters for read-only access
  getFullName(): string { return this.name.getFullName(); }
  getFirstName(): string { return this.name.firstName; }
  getLastName(): string { return this.name.lastName; }
  getMiddleName(): string | undefined { return this.name.middleName; }
  getEmail(): string { return this.email.value; }
  getRoleType(): 'Human' | 'AiAgent' { return this.roleType; }
  getJobTitle(): string | undefined { return this.jobTitle; }
  getDepartment(): string | undefined { return this.department; }
  getOrganizationId(): string | undefined { return this.organizationId; }
  isPersonActive(): boolean { return this.isActive; }
  getCreatedAt(): Date | undefined { return this.createdAt; }
  getUpdatedAt(): Date | undefined { return this.updatedAt; }

  // Snapshot for persistence
  toSnapshot(): any {
    return {
      id: this.id,
      firstName: this.name.firstName,
      lastName: this.name.lastName,
      middleName: this.name.middleName,
      email: this.email.value,
      roleType: this.roleType,
      isActive: this.isActive,
      jobTitle: this.jobTitle,
      department: this.department,
      organizationId: this.organizationId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      version: this.version
    };
  }
}